// src/publish/pieces/landingPage.js — pieces of the landing page (format key 'landing-landing').
//
// Backend fields (proposal, «Cosa manda il backend»): `hero` {headline (mirrored
// in `title`), subheadline, cta_text, cta_url}, `problem` and `solution`
// {heading, content (inline HTML)}, `features.items[]` {heading, content},
// `social_proof` {heading, bullets[]}, `faq[]` {question, answer}, `final_cta`
// {tagline, cta_text, cta_url}, `cta_url`; `body`, `cta`, `hashtags` are empty.
// Pieces, in this order:
//   page          PRIMARY, rich — every section in page order: H1 headline,
//                 subheadline, hero CTA as a link, H2 problem / solution with
//                 their text, H2 «Caratteristiche» + an H3 per feature, H2
//                 social-proof heading + its bullets, H2 «FAQ» + qa blocks,
//                 H2 tagline + final CTA as a link
//   hero          rich  — H2 headline, subheadline, "cta_text → URL"
//   problem       rich  — H2 heading + text
//   solution      rich  — H2 heading + text
//   features      rich  — an H3 + text per item (one card each)
//   social_proof  rich  — H2 heading + the bullet list
//   faq           list  — one qa block per question
//   final_cta     rich  — H2 tagline, "cta_text → URL"
// Every CTA URL is the brief's (ctaUrlOf: content.cta_url, else
// params.cta_url); the section's own cta_url is only a fallback. A section
// without any text produces no piece and no heading in the page; exactly one
// piece is primary (page, or the first piece when the page is empty).
// Every section piece is rich (not plain) so its «Copia» carries HTML too:
// pasted into Word its bold and its paragraphs survive, where the plain text
// with "\n\n" between paragraphs lands as empty lines and no emphasis.
// i18n: outputs:pieces.labels["landing-landing"].<id> (also the in-page
// headings «Caratteristiche», «Social proof», «FAQ») and
// outputs:pieces.destinations["landing-landing"].<id>.

import { normalizeText, inlineRuns } from '../normalize.js';
import { heading, paragraph, list, qa, link, text, compactBlocks } from '../blocks.js';
import { makePiece, pieceLabel, ctaUrlOf, ensureOnePrimary, trimmed as str, obj, arr } from '../piece.js';
import { normalizeUrl } from '../../utils/textProcessing.js';

const FORMAT = 'landing-landing';

/** The CTA of a section: {text, href} with the brief's URL first; text '' when none. */
function ctaOf(section, briefUrl) {
  const ctaText = str(section.cta_text);
  const href = briefUrl || normalizeUrl(str(section.cta_url));
  return { text: ctaText, href };
}

/** In the page: the CTA as a link paragraph (the text alone when there is no URL). */
const ctaLinkParagraph = ({ text: ctaText, href }) => paragraph(href ? [link(href, [text(ctaText)])] : [text(ctaText)]);

/** In a section piece: "cta_text → URL" as literal text (two fields to copy). */
const ctaArrowParagraph = ({ text: ctaText, href }) => paragraph([text(href ? `${ctaText} → ${href}` : ctaText)]);

/** H2 heading + text of a {heading, content} section (compacted). */
function headedBlocks(section, level = 2) {
  const out = [];
  const head = str(section.heading);
  if (head) out.push(heading(level, inlineRuns(head)));
  out.push(...normalizeText(str(section.content)));
  return compactBlocks(out);
}

export function build(entry) {
  const content = obj(entry?.results?.content);
  if (!Object.keys(content).length) return [];

  const briefUrl = ctaUrlOf(entry);
  const hero = obj(content.hero);
  const problem = obj(content.problem);
  const solution = obj(content.solution);
  const features = obj(content.features);
  const socialProof = obj(content.social_proof);
  const finalCta = obj(content.final_cta);

  // ── per-section blocks (each section piece reuses them) ──
  const headline = str(hero.headline) || str(content.title);
  const subheadline = str(hero.subheadline);
  const heroCta = ctaOf(hero, briefUrl);

  const problemBlocks = headedBlocks(problem);
  const solutionBlocks = headedBlocks(solution);

  const featureBlocks = arr(features.items)
    .map((it) => headedBlocks(obj(it), 3))
    .filter((blocks) => blocks.length);

  const bullets = arr(socialProof.bullets).map(str).filter(Boolean);
  const socialHeading = str(socialProof.heading);

  const faqBlocks = arr(content.faq)
    .map(obj)
    .map((f) => ({ q: str(f.question), a: normalizeText(str(f.answer)) }))
    .filter((f) => f.q || f.a.length)
    .map((f) => qa(inlineRuns(f.q), f.a));

  const tagline = str(finalCta.tagline);
  const finalCtaOf = ctaOf(finalCta, briefUrl);

  // ── the page ──
  const page = [];
  if (headline) page.push(heading(1, inlineRuns(headline)));
  if (subheadline) page.push(paragraph(inlineRuns(subheadline)));
  if (heroCta.text) page.push(ctaLinkParagraph(heroCta));
  page.push(...problemBlocks, ...solutionBlocks);
  if (featureBlocks.length) {
    page.push(heading(2, pieceLabel(FORMAT, 'features')));
    featureBlocks.forEach((blocks) => page.push(...blocks));
  }
  if (bullets.length) {
    page.push(heading(2, inlineRuns(socialHeading || pieceLabel(FORMAT, 'social_proof'))));
    page.push(list(bullets.map(inlineRuns)));
  }
  if (faqBlocks.length) {
    page.push(heading(2, pieceLabel(FORMAT, 'faq')));
    page.push(...faqBlocks);
  }
  if (tagline) page.push(heading(2, inlineRuns(tagline)));
  if (finalCtaOf.text) page.push(ctaLinkParagraph(finalCtaOf));

  // ── the pieces ──
  const pieces = [];
  const pageBlocks = compactBlocks(page);
  if (pageBlocks.length) pieces.push(makePiece(FORMAT, { id: 'page', kind: 'rich', blocks: pageBlocks }));

  const heroBlocks = compactBlocks([
    headline ? heading(2, inlineRuns(headline)) : null,
    subheadline ? paragraph(inlineRuns(subheadline)) : null,
    heroCta.text ? ctaArrowParagraph(heroCta) : null,
  ].filter(Boolean));
  if (heroBlocks.length) pieces.push(makePiece(FORMAT, { id: 'hero', kind: 'rich', blocks: heroBlocks }));

  if (problemBlocks.length) pieces.push(makePiece(FORMAT, { id: 'problem', kind: 'rich', blocks: problemBlocks }));
  if (solutionBlocks.length) pieces.push(makePiece(FORMAT, { id: 'solution', kind: 'rich', blocks: solutionBlocks }));
  if (featureBlocks.length) pieces.push(makePiece(FORMAT, { id: 'features', kind: 'rich', blocks: featureBlocks.flat() }));
  if (bullets.length) {
    const blocks = [];
    if (socialHeading) blocks.push(heading(2, inlineRuns(socialHeading)));
    blocks.push(list(bullets.map(inlineRuns)));
    pieces.push(makePiece(FORMAT, { id: 'social_proof', kind: 'rich', blocks }));
  }
  if (faqBlocks.length) pieces.push(makePiece(FORMAT, { id: 'faq', kind: 'list', blocks: faqBlocks }));

  const finalBlocks = compactBlocks([
    tagline ? heading(2, inlineRuns(tagline)) : null,
    finalCtaOf.text ? ctaArrowParagraph(finalCtaOf) : null,
  ].filter(Boolean));
  if (finalBlocks.length) pieces.push(makePiece(FORMAT, { id: 'final_cta', kind: 'rich', blocks: finalBlocks }));

  return ensureOnePrimary(pieces, ['page']);
}
